import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { buildLocalizedPath, type Lang, type RouteKey } from "~/config/route-config";
import { AppLink } from "~/components/ui/AppLink";
import { Button, NavPendingSweep } from "~/components/ui";
import { cn } from "~/lib/cn";
import { IconBack } from "~/components/layout/nav-icons";
import { startsInAppNavigation, useNavPendingSelf, useNavPendingTo } from "~/components/layout/use-nav-pending";

/** Full-width bar under the main navbar: back + optional context label (nested flows only). */
export function NestedTopBar({
  categoryLabel,
  categoryClassName,
  backLabel,
  backTo,
  fallbackRouteKey = "dashboard",
  className,
}: {
  categoryLabel?: ReactNode;
  /** Right label color (default: muted). */
  categoryClassName?: string;
  /** Left control label (defaults: `layout.back` for history, `layout.back_dashboard` when `backTo` is set). */
  backLabel?: ReactNode;
  /** If set, navigates to this path. If omitted, uses browser history back (`navigate(-1)`), then optional in-app fallback. */
  backTo?: string;
  /** When not using `backTo`, if history cannot go back (e.g. first entry), navigate to this route. */
  fallbackRouteKey?: RouteKey;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language === "en" ? "en" : "es") as Lang;
  const { pending: pendingSelf, markNavigationStart } = useNavPendingSelf();
  const defaultLabel = backTo != null ? t("layout.back_dashboard") : t("layout.back");
  const label = backLabel ?? defaultLabel;
  const fallbackPath = buildLocalizedPath(fallbackRouteKey, lang);

  const handleHistoryBack = () => {
    if (typeof window === "undefined") return;
    const raw = window.history.state;
    const idx =
      raw != null && typeof raw === "object" && "idx" in raw
        ? (raw as { idx?: number | null }).idx
        : undefined;
    markNavigationStart();
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(fallbackPath);
  };

  const back =
    backTo != null ? (
      <Button href={backTo} variant="text" className="back">
        <IconBack size={18} />
        {label}
      </Button>
    ) : (
      <Button
        type="button"
        variant="text"
        onClick={handleHistoryBack}
        className="back"
        isLoading={pendingSelf}
      >
        <IconBack size={18} />
        {label}
      </Button>
    );

  // T98 rebrand `.subhdr`: back control + context crumb (nested member flows).
  return (
    <div className={cn("subhdr", className)}>
      {back}
      {categoryLabel != null ? (
        <>
          <span className={cn("crumb", categoryClassName)}>{categoryLabel}</span>
          <span aria-hidden="true" className="subhdr-spacer" />
        </>
      ) : null}
    </div>
  );
}

/** Standard authenticated content width and vertical rhythm. */
export const memberPageShellClassName =
  "mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 md:py-12";

export function MemberPageShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(memberPageShellClassName, className)}>{children}</div>;
}

export type PageHeaderProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  titleAside?: ReactNode;
  description?: ReactNode;
  belowTitle?: ReactNode;
  end?: ReactNode;
  className?: string;
};

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  titleAside,
  description,
  belowTitle,
  end,
  className,
}: PageHeaderProps) {
  if (end != null) {
    return (
      <header
        className={cn(
          "mb-4 flex flex-col justify-between gap-4 md:mb-10 md:flex-row md:items-end md:gap-6",
          className,
        )}
      >
        <div className="min-w-0 flex-1">
          {eyebrow ? (
            <span className="mb-4 block tfc-label tfc-label-signature">
              {eyebrow}
            </span>
          ) : null}
          <h1 className="tfc-heading mb-1 text-2xl tracking-tight md:mb-2 md:text-5xl">
            {title}
          </h1>
          {belowTitle}
          {subtitle ? (
            <div className="mt-2 text-sm font-light uppercase tracking-widest text-textSecondary">{subtitle}</div>
          ) : null}
          {description ? <div className="mt-4">{description}</div> : null}
        </div>
        {end}
      </header>
    );
  }

  return (
    <header className={cn("mb-6 md:mb-10", className)}>
      {eyebrow ? (
        <span className="mb-4 block tfc-label tfc-label-signature">{eyebrow}</span>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <h1
          className={cn(
            "tfc-heading text-2xl tracking-tight md:text-5xl",
            titleAside ? "mb-0" : "mb-2",
          )}
        >
          {title}
        </h1>
        {titleAside}
      </div>
      {subtitle ? (
        <div className="mt-2 text-sm font-light uppercase tracking-widest text-textSecondary">{subtitle}</div>
      ) : null}
      {description ? <div className="mt-4">{description}</div> : null}
    </header>
  );
}

export function BottomBackAction({
  className,
  destination = "dashboard",
}: {
  className?: string;
  /** `dashboard` — member area; `home` — public landing (e.g. guest support). */
  destination?: "dashboard" | "home";
}) {
  const { t, i18n } = useTranslation();
  const routeKey = destination === "home" ? "home" : "dashboard";
  const label = destination === "home" ? t("layout.back_home") : t("layout.back_dashboard");
  // T558 — the same "take me back" job as `NestedTopBar`, at the bottom of the page and
  // just as silent until now.
  const { pending, onNavigate: startBack } = useNavPendingTo(
    buildLocalizedPath(routeKey, (i18n.language === "en" ? "en" : "es") as Lang),
  );
  return (
    <p
      className={cn(
        "mt-12 text-center text-[10px] font-bold uppercase tracking-widest text-textSecondary",
        className,
      )}
    >
      <AppLink
        routeKey={routeKey}
        className={cn("underline hover:opacity-60", pending && "nav-pending")}
        aria-busy={pending || undefined}
        onClick={(e) => { if (startsInAppNavigation(e)) startBack(); }}
      >
        {label}
        {pending ? <NavPendingSweep /> : null}
      </AppLink>
    </p>
  );
}
